import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';

/**
 * Shape of the value exposed by {@link AuthContext}.
 */
interface AuthContextType {
    /** The currently signed-in Firebase user, or `null` if unauthenticated. */
    currentUser: User | null;
    /** True while the initial `onAuthStateChanged` check is still pending. */
    loading: boolean;
    /** Error message surfaced from auth operations (e.g. unauthorised email), or `null`. */
    authError: string | null;
    /** Signs the current user out of Firebase Auth. */
    logout: () => Promise<void>;
    /**
     * Returns a fresh Firebase ID token for the current user, or `null` if
     * no user is signed in. Use this to authenticate HQ API requests.
     */
    getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
    currentUser: null,
    loading: true,
    authError: null,
    logout: async () => { },
    getToken: async () => null,
});

/**
 * Hook for consuming {@link AuthContext}. Must be used inside {@link AuthProvider}.
 *
 * @returns The current auth context value.
 */
export const useAuth = () => useContext(AuthContext);

/**
 * Provides Firebase authentication state to the component tree.
 *
 * - Subscribes to `onAuthStateChanged` on mount and cleans up on unmount.
 * - Blocks rendering of children until the initial auth check completes,
 *   preventing unauthenticated flashes on page load.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                setAuthError(null);
            } else {
                setCurrentUser(null);
                setAuthError(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const logout = async () => {
        await signOut(auth);
    };

    const getToken = async () => {
        if (currentUser) {
            return await currentUser.getIdToken();
        }
        return null;
    };

    return (
        <AuthContext.Provider value={{ currentUser, loading, authError, logout, getToken }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
